import {
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronDown,
  Clock,
  CreditCard,
  Heart,
  HelpCircle,
  LayoutDashboard,
  Lock,
  LogOut,
  Logo,
  Settings,
  Users,
} from "@ews-admin/global-design-system";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useHasLicence } from "../../hooks/useHasLicence";
import { scrollToTop } from "../../hooks/useScrollToTop";
import {
  requestTerminateSessionRequest,
  selectFeatureFlags,
  selectSessionAuthState,
} from "../../store/reducers/sessionSlice";
import { LanguageSwitcher } from "../molecules/LanguageSwitcher";

// Menu item ids gated behind an active manager licence (MED-1201).
// Only "Rapports et analyses" (reports) and "Paramètres" (facility) are in
// scope — staff/appointments have their own page-level button locks, and
// patients is handled separately.
const LICENCE_GATED_MENU_ITEM_IDS = ["reports", "facility"];

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  roles?: string[]; // If specified, only show for these roles
  featureFlag?: string; // If specified, only show when this feature flag is enabled
}

interface SidebarProps {
  onClose?: () => void;
  isMobile?: boolean;
}

const allMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    label: "sidebar.dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
  },
  {
    id: "appointments",
    label: "sidebar.appointments",
    icon: Calendar,
    path: "/appointments",
  },
  {
    id: "patients",
    label: "sidebar.patients",
    icon: Heart,
    path: "/patients",
  },
  {
    id: "availability",
    label: "sidebar.doctors",
    icon: Clock,
    path: "/secretary/availability",
    roles: ["secretary"],
  },
  {
    id: "staff",
    label: "sidebar.staffManagement",
    icon: Users,
    path: "/manager/staff",
    roles: ["manager"],
  },
  {
    id: "reports",
    label: "sidebar.reportsAnalytics",
    icon: BarChart3,
    path: "/manager/reports",
    roles: ["manager"],
  },
  {
    id: "facility",
    label: "sidebar.facilityManagement",
    icon: Settings,
    path: "/manager/settings",
    roles: ["manager"],
  },
  {
    id: "emergency",
    label: "sidebar.emergencyManagement",
    icon: AlertTriangle,
    path: "/manager/emergency",
    roles: ["manager"],
  },
  {
    id: "licence",
    label: "sidebar.licence",
    icon: CreditCard,
    path: "/manager/licence",
    roles: ["manager"],
    featureFlag: "MANAGER_LICENCE",
  },
];

export const Sidebar: React.FC<SidebarProps> = ({
  onClose,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const { session } = useSelector(selectSessionAuthState);
  const featureFlags = useSelector(selectFeatureFlags);
  const hasLicence = useHasLicence();
  const userRole = session?.user?.roleName?.toLowerCase() || "";

  // Filter menu items based on user role
  const menuItems = useMemo(() => {
    return allMenuItems.filter((item) => {
      // Hide items gated behind a disabled feature flag
      if (item.featureFlag && !featureFlags[item.featureFlag]) {
        return false;
      }
      // If no roles specified, show for all (backward compatibility)
      if (!item.roles || item.roles.length === 0) {
        // For items without role restriction, hide if there's a secretary-specific version
        if (userRole === "secretary") {
          return !allMenuItems.some(
            (other) =>
              other.id.startsWith("secretary-") &&
              other.id.includes(item.id.split("-")[0])
          );
        }
        return true;
      }
      // Show if user role matches
      return item.roles.includes(userRole);
    });
  }, [userRole, featureFlags]);

  // Helper function to safely translate
  const safeTranslate = (key: string) => {
    if (!i18n.isInitialized) {
      return key; // Return key if i18n is not ready
    }
    const translation = t(key);
    return translation === key ? key : translation;
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const handleLogout = () => {
    // Dispatch terminate session action - saga handles API call and redirect
    dispatch(requestTerminateSessionRequest());
  };

  const handleNavigation = (path: string, locked?: boolean) => {
    if (locked) {
      return; // Licence-gated item: block navigation (route is also guarded server/router-side)
    }
    navigate(path);
    if (isMobile && onClose) {
      onClose(); // Close sidebar on mobile after navigation
    }
    scrollToTop(); // Scroll to top after navigation
  };

  return (
    <div
      className={`bg-white border-r border-neutral-200 transition-all duration-300 flex flex-col h-full ${collapsed ? "w-16" : "w-64"
        } ${isMobile ? "w-64" : ""}`}
    >
      {/* Header */}
      <div
        className={`flex flex-shrink-0 items-center p-4 border-b border-neutral-200 ${collapsed && !isMobile ? "flex-col space-y-2" : "justify-between"
          }`}
      >
        {/* Full logo when the menu is open, the icon alone when it is collapsed */}
        {!collapsed || isMobile ? (
          <Logo size="sm" variant="normal" />
        ) : (
          <Logo size="sm" iconOnly />
        )}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-md transition-colors hover:bg-neutral-100"
          >
            <ChevronDown
              className={`w-4 h-4 text-neutral-600 transition-transform ${collapsed ? "rotate-90" : ""
                }`}
            />
          </button>
        )}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-md transition-colors hover:bg-neutral-100"
          >
            <ChevronDown className="w-4 h-4 text-neutral-600" />
          </button>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="overflow-y-auto flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          const isLocked =
            LICENCE_GATED_MENU_ITEM_IDS.includes(item.id) && !hasLicence;

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path, isLocked)}
              disabled={isLocked}
              title={isLocked ? t("licence.requiredTooltip") : undefined}
              aria-disabled={isLocked}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium sidebar-item ${isActive(item.path)
                  ? "sidebar-item-active"
                  : "text-neutral-700 sidebar-item-hover"
                } ${item.id === "staff" ? "mt-4" : ""} ${isLocked ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              <div className="flex items-center space-x-3">
                {isLocked ? (
                  <Lock className="w-5 h-5 sidebar-item-icon sidebar-item-icon-inactive" />
                ) : (
                  <item.icon
                    className={`w-5 h-5 sidebar-item-icon ${isActive(item.path)
                        ? "sidebar-item-icon-active"
                        : "sidebar-item-icon-inactive"
                      }`}
                  />
                )}
                {(!collapsed || isMobile) && (
                  <span>{safeTranslate(item.label)}</span>
                )}
              </div>
            </button>
          );
        })}
      </nav>

      {/* Bottom Actions - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 space-y-2 border-t border-neutral-200">
        {(!collapsed || isMobile) && (
          <>
            <LanguageSwitcher />
            <button
              onClick={() => handleNavigation("/help-support")}
              className="flex items-center px-3 py-2 space-x-3 w-full text-sm rounded-lg transition-colors text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900"
            >
              <HelpCircle className="w-5 h-5 text-neutral-500" />
              <span>{safeTranslate("sidebar.helpSupport")}</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center px-3 py-2 space-x-3 w-full text-sm rounded-lg transition-colors text-danger-600 hover:bg-danger-50"
            >
              <LogOut className="w-5 h-5" />
              <span>{safeTranslate("sidebar.logout")}</span>
            </button>
          </>
        )}
        {collapsed && !isMobile && (
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={() => handleNavigation("/help-support")}
              className="p-2 rounded-lg transition-colors text-neutral-700 hover:bg-neutral-50"
            >
              <HelpCircle className="w-5 h-5 text-neutral-500" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg transition-colors text-danger-600 hover:bg-danger-50"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
